import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import { Priority, Challenge, Outcome } from '../../types/customer';
import { Framework } from '../../types/framework';
import { Architecture } from '../../types/product';
import { RootState } from '../store';

interface ToolboxState {
  priorities: Priority[];
  prioritiesLoading: boolean;
  prioritiesError: string | null;

  challenges: Challenge[];
  challengesLoading: boolean;
  challengesError: string | null;

  frameworks: Framework[];
  frameworksLoading: boolean;
  frameworksError: string | null;

  outcomes: Outcome[];
  outcomesLoading: boolean;
  outcomesError: string | null;

  architectures: Architecture[];
  architecturesLoading: boolean;
  architecturesError: string | null;

  sentimentOptions: { id: number; name: string }[];
  sentimentOptionsLoading: boolean;
  sentimentOptionsError: string | null;

  prioritiesLookup: Record<number, Priority>;
  challengesLookup: Record<number, Challenge>;
  frameworksLookup: Record<number, Framework>;

  approvalStatus: Record<number, string>;
  approvalStatusLoading: boolean;
  approvalStatusError: string | null;
}

const initialState: ToolboxState = {
  priorities: [],
  prioritiesLoading: false,
  prioritiesError: null,

  challenges: [],
  challengesLoading: false,
  challengesError: null,

  frameworks: [],
  frameworksLoading: false,
  frameworksError: null,

  outcomes: [],
  outcomesLoading: false,
  outcomesError: null,

  architectures: [],
  architecturesLoading: false,
  architecturesError: null,

  sentimentOptions: [],
  sentimentOptionsLoading: false,
  sentimentOptionsError: null,

  prioritiesLookup: {},
  challengesLookup: {},
  frameworksLookup: {},

  approvalStatus: [],
  approvalStatusLoading: false,
  approvalStatusError: null,
};

// --- Async Thunks for fetching data ---

export const fetchPriorities = createAsyncThunk(
  'toolbox/fetchPriorities',
  async () => {
    const data = await fetchItems('db/priority');
    return data as Priority[];
  }
);

export const fetchChallenges = createAsyncThunk(
  'toolbox/fetchChallenges',
  async () => {
    const data = await fetchItems("db/challenge", { detailed: true, depth: 2 });
    return data as Challenge[];
  }
);

export const fetchFrameworks = createAsyncThunk(
  'toolbox/fetchFrameworks',
  async () => {
    const data = await fetchItems('db/framework');
    return data as Framework[];
  }
);

export const fetchArchitectures = createAsyncThunk(
  'toolbox/fetchArchitectures',
  async () => {
    const data = await fetchItems('db/architecture');
    return data as Architecture[];
  }
);

export const fetchSentimentOptions = createAsyncThunk(
  'toolbox/fetchSentimentOptions',
  async () => {
    const data = await fetchItems('db/customer-sentiment');
    return data.map((item: any) => ({
      id: item.id,
      name: item.name,
    })) as { id: number; name: string }[];
  }
);

export const fetchOutcomes = createAsyncThunk(
  'toolbox/fetchOutcomes',
  async () => {
    const data = await fetchItems('db/outcome', { detailed: true, depth: 2 });
    return data as Outcome[];
  }
);

export const fetchApprovalStatus = createAsyncThunk(
  'toolbox/fetchApprovalStatus',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchItems('db/approval-status');
      return data as { id: number; title: string }[];
    } catch (error) {
      return rejectWithValue('Failed to fetch approval status');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { approvalStatus, approvalStatusLoading } = state.toolbox;

      if (approvalStatusLoading) return false;
      if (Object.keys(approvalStatus).length > 0) return false;

      return true;
    },
  }
);

const toolboxSlice = createSlice({
  name: 'toolbox',
  initialState,
  reducers: {
    // Optional: add reducers if you need to update toolbox data locally
    setPriorities(state, action: PayloadAction<Priority[]>) {
      state.priorities = action.payload;
    },
    setChallenges(state, action: PayloadAction<Challenge[]>) {
      state.challenges = action.payload;
    },
    setFrameworks(state, action: PayloadAction<Framework[]>) {
      state.frameworks = action.payload;
    },
    setArchitectures(state, action: PayloadAction<Architecture[]>) {
      state.architectures = action.payload;
    },
    setOutcomes(state, action: PayloadAction<Outcome[]>) {
      state.outcomes = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPriorities.pending, (state) => {
        state.prioritiesLoading = true;
        state.prioritiesError = null;
      })
      .addCase(fetchPriorities.fulfilled, (state, action) => {
        state.priorities = action.payload;
        state.prioritiesLoading = false;
        state.prioritiesLookup = action.payload.reduce((acc, priority) => {
          acc[priority.id] = priority;
          return acc;
        }, {} as Record<number, Priority>);
      })
      .addCase(fetchPriorities.rejected, (state, action) => {
        state.prioritiesLoading = false;
        state.prioritiesError = action.error.message ?? 'Failed to fetch priorities';
      })

      .addCase(fetchChallenges.pending, (state) => {
        state.challengesLoading = true;
        state.challengesError = null;
      })
      .addCase(fetchChallenges.fulfilled, (state, action) => {
        state.challenges = action.payload;
        state.challengesLoading = false;
        state.challengesLookup = action.payload.reduce((acc, challenge) => {
          acc[challenge.id] = challenge;
          return acc;
        }, {} as Record<number, Challenge>);
      })
      .addCase(fetchChallenges.rejected, (state, action) => {
        state.challengesLoading = false;
        state.challengesError = action.error.message ?? 'Failed to fetch challenges';
      })

      .addCase(fetchFrameworks.pending, (state) => {
        state.frameworksLoading = true;
        state.frameworksError = null;
      })
      .addCase(fetchFrameworks.fulfilled, (state, action) => {
        state.frameworks = action.payload;
        state.frameworksLoading = false;
        state.frameworksLookup = action.payload.reduce((acc, framework) => {
          acc[framework.id] = framework;
          return acc;
        }, {} as Record<number, Framework>);
      })
      .addCase(fetchFrameworks.rejected, (state, action) => {
        state.frameworksLoading = false;
        state.frameworksError = action.error.message ?? 'Failed to fetch frameworks';
      })

      .addCase(fetchArchitectures.pending, (state) => {
        state.architecturesLoading = true;
        state.architecturesError = null;
      })
      .addCase(fetchArchitectures.fulfilled, (state, action) => {
        state.architectures = action.payload;
        state.architecturesLoading = false;
      })
      .addCase(fetchArchitectures.rejected, (state, action) => {
        state.architecturesLoading = false;
        state.architecturesError = action.error.message ?? 'Failed to fetch architectures';
      })

      .addCase(fetchSentimentOptions.pending, (state) => {
        state.sentimentOptionsLoading = true;
        state.sentimentOptionsError = null;
      })
      .addCase(fetchSentimentOptions.fulfilled, (state, action) => {
        state.sentimentOptions = action.payload;
        state.sentimentOptionsLoading = false;
      })
      .addCase(fetchSentimentOptions.rejected, (state, action) => {
        state.sentimentOptionsLoading = false;
        state.sentimentOptionsError = action.error.message ?? 'Failed to fetch sentiment options';
      })

      .addCase(fetchOutcomes.pending, (state) => {
        state.outcomesLoading = true;
        state.outcomesError = null;
      })
      .addCase(fetchOutcomes.fulfilled, (state, action) => {
        state.outcomes = action.payload;
        state.outcomesLoading = false;
      })
      .addCase(fetchOutcomes.rejected, (state, action) => {
        state.outcomesLoading = false;
        state.outcomesError = action.error.message ?? 'Failed to fetch outcomes';
      })

      .addCase(fetchApprovalStatus.pending, (state) => {
        state.approvalStatusLoading = true;
        state.approvalStatusError = null;
      })
      .addCase(fetchApprovalStatus.fulfilled, (state, action) => {
        state.approvalStatus = action.payload.reduce((acc, status) => {
          acc[status.id] = status.title;
          return acc;
        }, {} as Record<number, string>);
        state.approvalStatusLoading = false;
      })
      .addCase(fetchApprovalStatus.rejected, (state, action) => {
        state.approvalStatusLoading = false;
        state.approvalStatusError = action.error.message ?? 'Failed to fetch approval status';
      }); 
  },
});

export const {
  setPriorities,
  setChallenges,
  setFrameworks,
  setOutcomes,
  setArchitectures
} = toolboxSlice.actions;

export default toolboxSlice.reducer;